import { listen, type UnlistenFn } from '@tauri-apps/api/event'
import { invoke } from '~/lib/tauriInvoke'
import { isTauriRuntime } from '~/lib/platform/runtime'

// iOS screen share that keeps running while the app is in the background.
//
// `getDisplayMedia()` works on iOS only while the app is in the foreground: the moment it's
// backgrounded, iOS stops the capture itself (replayd: "stop capture for client capture full
// display entered background"). The only capture iOS keeps alive in the background is a ReplayKit
// Broadcast Upload Extension — so on iOS the share goes:
//
//   extension (gen/apple/VoiceBroadcast) --frames--> native app side (Sources/app/ScreenBroadcast.swift)
//   native side --local WebRTC peer connection--> this module (the received video track)
//   this module's track --> LiveKit, via the normal `startScreenShare` path, as the user's own share
//
// This module owns the local peer connection and the broadcast's lifecycle; LiveKit publishing
// stays in `LiveKitManager` like any other custom screen share. Checked on a real iPhone before
// building it: WebKit re-sends a track received on one RTCPeerConnection through another at full
// frame rate (H264, hardware), and keeps doing so while the app is backgrounded.

const EVENT_NAME = 'ios-screen-broadcast'
/** How long to wait for the user to start the broadcast from the system sheet. */
const START_TIMEOUT_MS = 120_000
const ICE_GATHERING_TIMEOUT_MS = 3_000

type BroadcastEvent = { type: 'started' | 'stopped' | 'error'; message?: string }

export class IosBroadcastCancelledError extends Error {
  constructor() {
    super('Трансляция экрана не была запущена')
    this.name = 'IosBroadcastCancelledError'
  }
}

const isIOS = typeof navigator !== 'undefined' && /iPhone|iPad|iPod/i.test(navigator.userAgent)

export function isIosScreenBroadcastAvailable(): boolean {
  return isIOS && isTauriRuntime()
}

let peerConnection: RTCPeerConnection | null = null
let unlistenEvents: UnlistenFn | null = null
let onStoppedHandler: (() => void) | null = null
/** Bumped by every start/stop, so a start that's still waiting on the user can tell it was superseded. */
let generation = 0

function waitForIceGathering(pc: RTCPeerConnection) {
  return new Promise<void>((resolve) => {
    if (pc.iceGatheringState === 'complete') return resolve()
    const timer = setTimeout(resolve, ICE_GATHERING_TIMEOUT_MS)
    pc.addEventListener('icegatheringstatechange', () => {
      if (pc.iceGatheringState === 'complete') {
        clearTimeout(timer)
        resolve()
      }
    })
  })
}

/**
 * Sets up the local link, shows the system "Start Broadcast" sheet and resolves with the screen's
 * video stream once the user has started the broadcast and frames are flowing.
 *
 * `onStopped` fires if the broadcast ends on its own afterwards — the user stops it from the red
 * status-bar pill / Control Center, or iOS ends it. It is not called for `stopIosScreenBroadcast()`.
 *
 * Rejects with `IosBroadcastCancelledError` if the broadcast isn't started (sheet dismissed).
 */
export async function startIosScreenBroadcast(onStopped: () => void): Promise<MediaStream> {
  await stopIosScreenBroadcast()
  const myGeneration = ++generation

  let resolveStarted!: () => void
  let rejectStarted!: (error: Error) => void
  const started = new Promise<void>((resolve, reject) => {
    resolveStarted = resolve
    rejectStarted = reject
  })

  unlistenEvents = await listen<BroadcastEvent>(EVENT_NAME, ({ payload }) => {
    if (myGeneration !== generation) return
    if (payload.type === 'started') {
      resolveStarted()
    } else if (payload.type === 'stopped') {
      rejectStarted(new IosBroadcastCancelledError())
      const handler = onStoppedHandler
      void stopIosScreenBroadcast()
      handler?.()
    } else if (payload.type === 'error') {
      console.warn('[iosScreenBroadcast]', payload.message)
      rejectStarted(new Error(payload.message ?? 'Ошибка трансляции экрана'))
    }
  })

  try {
    const pc = new RTCPeerConnection({ iceServers: [] })
    peerConnection = pc
    pc.addTransceiver('video', { direction: 'recvonly' })
    const receivedTrack = new Promise<MediaStreamTrack>((resolve) => {
      pc.ontrack = (event) => resolve(event.track)
    })

    await pc.setLocalDescription(await pc.createOffer())
    await waitForIceGathering(pc)
    const answer = await invoke<string>('ios_screen_broadcast_answer', { offer: pc.localDescription!.sdp })
    if (myGeneration !== generation) throw new IosBroadcastCancelledError()
    await pc.setRemoteDescription({ type: 'answer', sdp: answer })
    const track = await receivedTrack

    await invoke('ios_screen_broadcast_start')
    const timeout = setTimeout(() => rejectStarted(new IosBroadcastCancelledError()), START_TIMEOUT_MS)
    try {
      await started
    } finally {
      clearTimeout(timeout)
    }
    if (myGeneration !== generation) throw new IosBroadcastCancelledError()

    onStoppedHandler = onStopped
    return new MediaStream([track])
  } catch (error) {
    if (myGeneration === generation) await stopIosScreenBroadcast()
    throw error
  }
}

/** Ends the broadcast and the local link. Idempotent; safe to call when nothing is running. */
export async function stopIosScreenBroadcast() {
  const hadBroadcast = peerConnection !== null
  generation++
  onStoppedHandler = null
  unlistenEvents?.()
  unlistenEvents = null
  peerConnection?.close()
  peerConnection = null
  if (hadBroadcast) {
    await invoke('ios_screen_broadcast_stop').catch((error) => {
      console.warn('[iosScreenBroadcast] stop failed', error)
    })
  }
}

export function isIosScreenBroadcastActive(): boolean {
  return peerConnection !== null
}
